/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { findEligibleDiscounts } from "../../../../../api/action/discount";
import { getOrderById, orderReversal, updateOrder } from "../../../../../api/action/order";
import { initiatePayment, setReturnURL } from "../../../../../api/action/user";
import {
    buildZellePendingTransaction,
    getCurrencyForCountry,
    getDefaultGatewayPaymentMethod,
    getGatewayProvider,
    getManualPaymentDisplayDetails,
    getOfflineOrderStatus,
    getSelectedPaymentOption,
    getSelectablePaymentOptions,
    getZellePaymentMethod,
    isCashPaymentOption,
    normalizeOfflineReferenceId,
    requiresOfflineReference
} from "../../../../payment/paymentMethodUtils";
import {
    formatCurrency as formatSharedCurrency,
    formatNumber as formatSharedNumber
} from "../../../../../utils/format";
import Loading from "../../../InstitutionDomain/Enquiry/components/Loading";
import { isKioskSessionActive } from "../../../../../domains/flexOffice/kiosk/kioskSession.js";
import { getScheduledProductFromOrder } from "../bookings/utils/orderUtils.jsx";
import { triggerHaptic } from "../../../../../utils/haptics.js";

// ── Extracted Components ──────────────────────────────────────────────────────
import ExpiryWarningDialog from "./components/ExpiryWarningDialog";
import OffersModal from "./components/OffersModal";
import OrderDetailsCard from "./components/OrderDetailsCard";
import PaymentSummaryCard from "./components/PaymentSummaryCard";
import Spinner from "./components/Spinner";
import { SessionExpiredToast, SuccessToast } from "./components/Toasts";
import TrustBadges from "./components/TrustBadges";
const BOOKING_PROCESSING_NOTICE_KEY = 'flex_booking_processing_notice';
const EMPTY_PAYMENT_METHODS = [];

// ─── Main Component ───────────────────────────────────────────────────────────
const ConfirmBook = () => {
    const [orderDetails, setOrderDetails] = useState(null);
    const [shoppingCartData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [showCashConfirmDialog, setShowCashConfirmDialog] = useState(false);
    const [showZelleModal, setShowZelleModal] = useState(false);
    const [alertMsg, setAlertMsg] = useState({ open: false, message: "", severity: "error" });
    const [paymentMethod, setPaymentMethod] = useState('');
    const [zelleReferenceId, setZelleReferenceId] = useState('');
    const [cartTotalPrice] = useState(0);
    const [remainingTime, setRemainingTime] = useState(null);
    const [showExpiredNotification, setShowExpiredNotification] = useState(false);
    const [showExpiryWarningDialog, setShowExpiryWarningDialog] = useState(false);
    const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
    const [defaultProductImage, setDefaultProductImage] = useState(null);

    // Multi-select: store a Set of applied discount IDs for the entire grouped order
    const [appliedDiscountIds, setAppliedDiscountIds] = useState(new Set());
    // Pending selection inside modal (not yet confirmed)
    const [pendingDiscountIds, setPendingDiscountIds] = useState(new Set());
    const [showOffersModal, setShowOffersModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    // Kiosk-only: a full-screen "you're booked" confirmation shown instead of
    // the brief SuccessToast + auto-navigate, since a kiosk guest walking away
    // from a shared device needs a clear, deliberate stopping point rather than
    // a toast that vanishes and silently redirects.
    const [showKioskBookingSuccess, setShowKioskBookingSuccess] = useState(false);
    // Discounts fetched directly in this component
    const [fetchedDiscounts, setFetchedDiscounts] = useState([]);
    const [isFetchingDiscounts, setIsFetchingDiscounts] = useState(false);
    // Store original discount details from order response
    const [originalDiscountsMap, setOriginalDiscountsMap] = useState(new Map());

    const selectionTimeoutRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const notificationTimeoutRef = useRef(null);
    const successTimeoutRef = useRef(null);
    const discountsFetchedRef = useRef(false);
    const getOrderIdRef = useRef(null);
    const fetchOrderDetailsRef = useRef(null);
    const fetchDiscountsForOrderRef = useRef(null);
    const startSessionTimeoutRef = useRef(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    // Kiosk guests pay on a shared touchscreen device — enlarge the tappable
    // controls on this page rather than the compact web sizing.
    const isKiosk = isKioskSessionActive();

    const userDetail = useSelector((s) => s?.auth?.data);
    const isGuestUser = !userDetail?.primaryId;
    const displayName = userDetail?.firstName || 'there';
    const companyState = useSelector((s) => s.companyReducer.company);
    const reduxOrderDetail = useSelector((s) => s.order);
    const companyOperatingLocation = companyState?.operatingLocation?.code || 'IN';
    const businessdomain = localStorage.getItem("domainType");
    const storedCompanyDetails = companyState;
    const companyDetails = useSelector((state) => state?.companyReducer?.company);
    const operatingLocationCode = companyDetails?.operatingLocation?.code;
    const companyPaymentMethods = storedCompanyDetails?.account?.paymentMethods || EMPTY_PAYMENT_METHODS;
    const isRecurringBooking = Boolean(
        orderDetails?.isGroupedOrder &&
        Array.isArray(orderDetails?.childOrders) &&
        orderDetails.childOrders.length > 0
    );
    const shouldHideCashPaymentOption = isRecurringBooking;
    const paymentOptions = getSelectablePaymentOptions(
        companyOperatingLocation,
        companyPaymentMethods,
        storedCompanyDetails
    );
    const availablePaymentOptions = (
        shouldHideCashPaymentOption
            ? paymentOptions.filter((option) => !isCashPaymentOption(option))
            : paymentOptions
    );
    const selectedPaymentOption = getSelectedPaymentOption(availablePaymentOptions, paymentMethod);
    const manualPaymentOption = requiresOfflineReference(selectedPaymentOption)
        ? selectedPaymentOption
        : availablePaymentOptions.find((option) => option.requiresReference) || null;
    const manualPaymentDetails = getManualPaymentDisplayDetails(
        storedCompanyDetails,
        manualPaymentOption,
        orderDetails?.number || orderDetails?.orderDetails?.[0]?.number || ''
    );
    const zellePaymentMethod = getZellePaymentMethod(companyPaymentMethods);
    const configuredManualPaymentMethod = manualPaymentOption?.paymentMethodConfig || null;
    const configuredRecipientName = configuredManualPaymentMethod?.nameOnAccount
        || configuredManualPaymentMethod?.name
        || '';
    const configuredRecipientTag = configuredManualPaymentMethod?.accountTag || '';
    const configuredReferenceLabel = configuredManualPaymentMethod?.routingNumber
        ? `Routing • ${configuredManualPaymentMethod.routingNumber}`
        : configuredManualPaymentMethod?.accountNumber
            ? `Acct • ${String(configuredManualPaymentMethod.accountNumber).slice(-4)}`
            : configuredManualPaymentMethod?.accountTag
                || configuredManualPaymentMethod?.email
                || configuredManualPaymentMethod?.emailAddress
                || (configuredManualPaymentMethod?.phoneNumber ? String(configuredManualPaymentMethod.phoneNumber) : '');
    const configuredRecipientEmail = configuredManualPaymentMethod?.email
        || configuredManualPaymentMethod?.emailAddress
        || '';
    const configuredRecipientPhone = configuredManualPaymentMethod?.phoneNumber || '';
    const configuredRecipientCountryCode = configuredManualPaymentMethod?.countryCode || '';
    const hasConfiguredRecipientDetails = Boolean(
        configuredRecipientName
        || configuredReferenceLabel
        || configuredRecipientTag
        || configuredRecipientPhone
        || configuredRecipientEmail
    );
    const normalizedZelleReferenceId = normalizeOfflineReferenceId(zelleReferenceId);
    const showZelleQuickAccess = Boolean(manualPaymentOption);

    // Keep all hooks together near the top of the component so hook order is explicit.
    /* eslint-disable react-hooks/rules-of-hooks */
    useEffect(() => {
        const loadInitialOrder = async () => {
            try {
                const id = getOrderIdRef.current?.();
                if (id) {
                    await fetchOrderDetailsRef.current?.(id);
                }
            } catch {
                setAlertMsg({ open: true, message: "Error loading order.", severity: "error" });
            }
        };
        loadInitialOrder();
    }, []);

    useEffect(() => {
        if (!orderDetails || discountsFetchedRef.current) return;

        let cancelled = false;

        const loadInitialDiscounts = async () => {
            const fetched = await fetchDiscountsForOrderRef.current?.(orderDetails);
            if (!cancelled && fetched) {
                discountsFetchedRef.current = true;
            }
        };

        loadInitialDiscounts();

        return () => {
            cancelled = true;
        };
    }, [orderDetails]);

    useEffect(() => {
        // Clean up timers when the booking screen unmounts.
        return () => {
            clearSelectionTimeout();
            if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        // Restart the booking timer when the current order becomes active again.
        if (orderDetails && !isProcessingPayment) startSessionTimeoutRef.current?.();
        return () => clearSelectionTimeout();
    }, [orderDetails, isProcessingPayment]);

    useEffect(() => {
        const nextPaymentOptions = getSelectablePaymentOptions(
            companyOperatingLocation,
            companyPaymentMethods,
            storedCompanyDetails
        );
        const nextAvailablePaymentOptions = shouldHideCashPaymentOption
            ? nextPaymentOptions.filter((option) => !isCashPaymentOption(option))
            : nextPaymentOptions;
        const preferredGatewayMethod = getDefaultGatewayPaymentMethod(companyOperatingLocation, companyPaymentMethods);
        const nextPaymentMethod = nextAvailablePaymentOptions.find((option) => option.value === preferredGatewayMethod)?.value
            || nextAvailablePaymentOptions[0]?.value
            || '';

        setPaymentMethod((currentMethod) => (
            nextAvailablePaymentOptions.some((option) => option.value === currentMethod)
                ? currentMethod
                : nextPaymentMethod
        ));
    }, [companyOperatingLocation, companyPaymentMethods, shouldHideCashPaymentOption, storedCompanyDetails]);
    /* eslint-enable react-hooks/rules-of-hooks */











    // ─── Timer helpers ─────────────────────────────────────────────────────────
    const clearSelectionTimeout = () => {
        if (selectionTimeoutRef.current) clearTimeout(selectionTimeoutRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        setRemainingTime(null);
    };

    const setBookingProcessingNotice = (orderId, orderNumber) => {
        try {
            localStorage.setItem(BOOKING_PROCESSING_NOTICE_KEY, JSON.stringify({
                createdAt: Date.now(),
                orderId: orderId ? String(orderId) : '',
                orderNumber: orderNumber || ''
            }));
        } catch { }
    };

    const startCountdownTimer = (ms) => {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        const end = Date.now() + ms;
        countdownIntervalRef.current = setInterval(() => {
            const left = end - Date.now();
            if (left <= 0) {
                clearInterval(countdownIntervalRef.current);
                setRemainingTime(0);
                setShowExpiryWarningDialog(false);
                handleSessionExpired();
            } else {
                const minutes = Math.floor(left / 60000);
                const seconds = Math.floor((left % 60000) / 1000);
                setRemainingTime({ minutes, seconds });
                if (minutes === 2 && seconds === 0) setShowExpiryWarningDialog(true);
            }
        }, 1000);
    };

    const startSessionTimeout = () => {
        clearSelectionTimeout();
        const ms = 15 * 60 * 1000;
        selectionTimeoutRef.current = setTimeout(handleSessionExpired, ms);
        startCountdownTimer(ms);
    };

    const handleSessionExpired = async () => {
        clearSelectionTimeout();
        const id = getOrderId();
        if (id) { try { await dispatch(orderReversal({ primaryId: id })); } catch { } }
        setShowExpiredNotification(true);
        notificationTimeoutRef.current = setTimeout(() => setShowExpiredNotification(false), 5000);
        navigate('/flex-home');
    };



    // ─── Order ID resolver ─────────────────────────────────────────────────────
    const getOrderId = () => {
        try {
            const s = sessionStorage.getItem("orderPrimaryId"); if (s) return s;
            const p = localStorage.getItem('persistedOrder');
            if (p) { const parsed = JSON.parse(p); return parsed?.primaryId || parsed?.orderDetails?.[0]?.primaryId || null; }
            const l = JSON.parse(localStorage.getItem("orderDetails")); if (l?.primaryId) return l.primaryId;
            return reduxOrderDetail?.orderDetails?.[0]?.primaryId || null;
        } catch { return null; }
    };

    const getProductImageFromOrder = (order) => {
        const sp = order.lineItemList?.[0]?.scheduledProduct;
        if (sp?.product?.imageContents?.[0]?.displayLink)
            return sp.product.imageContents[0].displayLink;
        if (sp?.product?.imageUrlsList?.[0]) {
            const url = sp.product.imageUrlsList[0];
            return url.startsWith('http') ? url : `https://managemybiz.s3.amazonaws.com/${url}`;
        }
        return null;
    };
    const companyId = companyDetails?.primaryId;

    // Helper to extract discount details from order
    const extractDiscountsFromOrder = (order) => {
        const discountsMap = new Map();

        const processLineItem = (lineItem) => {
            if (lineItem?.eligibleDiscounts && Array.isArray(lineItem.eligibleDiscounts)) {
                lineItem.eligibleDiscounts.forEach(disc => {
                    if (disc.primaryId && !discountsMap.has(disc.primaryId)) {
                        discountsMap.set(disc.primaryId, disc);
                    }
                });
            }
            if (lineItem?.discounts && Array.isArray(lineItem.discounts)) {
                lineItem.discounts.forEach(disc => {
                    if (disc.primaryId && !discountsMap.has(disc.primaryId)) {
                        discountsMap.set(disc.primaryId, disc);
                    }
                });
            }
            if (lineItem?.discount?.primaryId && !discountsMap.has(lineItem.discount.primaryId)) {
                discountsMap.set(lineItem.discount.primaryId, lineItem.discount);
            }
        };

        if (order.isGroupedOrder && order.childOrders?.length > 0) {
            order.childOrders.forEach(child => {
                if (child.lineItemList) {
                    child.lineItemList.forEach(processLineItem);
                }
            });
        } else if (order.orderDetails?.[0]) {
            const singleOrder = order.orderDetails[0];
            if (singleOrder.lineItemList) {
                singleOrder.lineItemList.forEach(processLineItem);
            }
        } else if (order.lineItemList) {
            order.lineItemList.forEach(processLineItem);
        }

        return discountsMap;
    };

    const normalizeDiscountResults = (result) => {
        if (Array.isArray(result)) return result;
        if (Array.isArray(result?.eligibleDiscounts)) return result.eligibleDiscounts;
        if (Array.isArray(result?.payload)) return result.payload;
        if (Array.isArray(result?.data)) return result.data;
        if (result?.primaryId) return [result];
        return [];
    };

    const dedupeDiscounts = (discounts = []) => {
        const discountMap = new Map();

        discounts.forEach((discount) => {
            if (discount?.primaryId && !discountMap.has(discount.primaryId)) {
                discountMap.set(discount.primaryId, discount);
            }
        });

        return Array.from(discountMap.values());
    };

    const areDiscountSetsEqual = (left = new Set(), right = new Set()) => {
        if (left.size !== right.size) return false;
        for (const id of left) {
            if (!right.has(id)) return false;
        }
        return true;
    };

    const getAllDiscountIds = (discounts = []) => new Set(
        discounts
            .map((discount) => discount?.primaryId)
            .filter(Boolean)
    );

    const normalizeDiscountSelection = (selectedIds = new Set(), discounts = []) => {
        const allDiscountIds = getAllDiscountIds(discounts);
        if (allDiscountIds.size === 0) return new Set();

        return new Set(Array.from(selectedIds).filter((discountId) => allDiscountIds.has(discountId)));
    };

    const parseDiscountDate = (value, isEndDate = false) => {
        if (!value) return null;

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return null;

            if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
                const normalized = `${trimmed}${isEndDate ? 'T23:59:59.999' : 'T00:00:00.000'}`;
                const parsed = new Date(normalized);
                return Number.isNaN(parsed.getTime()) ? null : parsed;
            }

            const parsed = new Date(trimmed);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const getDiscountWindow = (discount) => {
        const effectiveDate = discount?.timePeriod?.effectiveDate || {};

        return {
            startDate: parseDiscountDate(
                effectiveDate.localStartDate
                || effectiveDate.effectiveStartDate
                || effectiveDate.startDate
                || discount?.startDate
            ),
            endDate: parseDiscountDate(
                effectiveDate.localEndDate
                || effectiveDate.effectiveEndDate
                || effectiveDate.endDate
                || discount?.endDate,
                true
            )
        };
    };


    const roundMoney = (v) => Math.round(((parseFloat(v) || 0) + Number.EPSILON) * 100) / 100;

    const resolveSubtotal = (obj) => {
        const subtotal = parseFloat(obj?.subtotalAmount);
        if (!isNaN(subtotal) && subtotal > 0) return roundMoney(subtotal);

        const total = parseFloat(obj?.totalAmount);
        if (!isNaN(total) && total > 0) return roundMoney(total);

        const afterDiscount = parseFloat(obj?.totalAmtAfterDiscount);
        if (!isNaN(afterDiscount) && afterDiscount > 0) {
            const discountAmt = parseFloat(obj?.totalDiscountAmt);
            if (!isNaN(discountAmt) && discountAmt > 0) {
                return roundMoney(afterDiscount + discountAmt);
            }
            return roundMoney(afterDiscount);
        }

        return 0;
    };

    const resolvePayable = (obj) => {
        const afterDiscount = parseFloat(obj?.totalAmtAfterDiscount);
        if (!isNaN(afterDiscount) && afterDiscount > 0) return roundMoney(afterDiscount);

        const due = parseFloat(obj?.dueAmount);
        if (!isNaN(due) && due > 0) return roundMoney(due);

        const total = parseFloat(obj?.totalAmount);
        if (!isNaN(total) && total > 0) return roundMoney(total);

        const subtotal = parseFloat(obj?.subtotalAmount);
        if (!isNaN(subtotal) && subtotal > 0) return roundMoney(subtotal);

        return 0;
    };

    const resolveDiscountAmount = (obj) => {
        const explicitDiscount = parseFloat(obj?.totalDiscountAmt);
        if (!isNaN(explicitDiscount) && explicitDiscount > 0) {
            return roundMoney(explicitDiscount);
        }

        const subtotal = resolveSubtotal(obj);
        const payable = resolvePayable(obj);
        if (subtotal > payable) {
            return roundMoney(subtotal - payable);
        }

        return 0;
    };

    const getResponseDiscountAmount = (obj) => {
        const subtotal = resolveSubtotal(obj);
        const afterDiscount = parseFloat(obj?.totalAmtAfterDiscount);

        if (!isNaN(afterDiscount) && subtotal > afterDiscount) {
            return roundMoney(subtotal - afterDiscount);
        }

        const responseDiscount = parseFloat(obj?.totalDiscountAmt);
        if (!isNaN(responseDiscount) && responseDiscount > 0) {
            return roundMoney(responseDiscount);
        }

        return 0;
    };

    // ─── Data fetching ─────────────────────────────────────────────────────────
    const fetchOrderDetails = async (orderId) => {
        try {
            setLoading(true);
            const res = await dispatch(getOrderById(orderId, companyId));

            if (res) {
                let d;
                if (res.data) {
                    d = Array.isArray(res.data) ? res.data[0] : res.data;
                } else if (Array.isArray(res)) {
                    d = res[0];
                } else {
                    d = res;
                }

                if (d) {
                    let obj;
                    if (d.isGroupedOrder === true && Array.isArray(d.childOrders) && d.childOrders.length > 0) {
                        obj = d;
                    } else if (d.orderDetails) {
                        obj = d;
                    } else {
                        obj = { orderDetails: Array.isArray(d) ? d : [d] };
                    }

                    setOrderDetails(obj);

                    // Extract discount details from the order response
                    const discountsFromOrder = extractDiscountsFromOrder(obj);
                    setOriginalDiscountsMap(discountsFromOrder);

                    if (obj.isGroupedOrder && obj.childOrders?.length > 0) {
                        const image = getProductImageFromOrder(obj.childOrders[0]);
                        setDefaultProductImage(image);

                        const allDiscountIds = new Set();
                        obj.childOrders.forEach(childOrder => {
                            const lineItem = childOrder.lineItemList?.[0];
                            if (lineItem?.discounts?.length > 0) {
                                lineItem.discounts.forEach(disc => disc.primaryId && allDiscountIds.add(disc.primaryId));
                            } else if (lineItem?.discount?.primaryId) {
                                allDiscountIds.add(lineItem.discount.primaryId);
                            }
                        });

                        if (allDiscountIds.size === 0 && discountsFromOrder.size > 0) {
                            const payable = resolvePayable(obj);
                            const rawTotal = resolveSubtotal(obj);
                            if (rawTotal > payable) {
                                discountsFromOrder.forEach((_, id) => allDiscountIds.add(id));
                            }
                        }
                        setAppliedDiscountIds(
                            normalizeDiscountSelection(allDiscountIds, Array.from(discountsFromOrder.values()))
                        );

                    } else {
                        const order = obj.orderDetails?.[0] || d;
                        const lineItem = order?.lineItemList?.[0];
                        let selectedDiscountIds = new Set();
                        if (lineItem?.discounts?.length > 0) {
                            selectedDiscountIds = new Set(lineItem.discounts.map(d => d.primaryId).filter(Boolean));
                        } else if (lineItem?.discount?.primaryId) {
                            selectedDiscountIds = new Set([lineItem.discount.primaryId]);
                        } else if (resolveDiscountAmount(order) > 0 && lineItem?.eligibleDiscounts?.length > 0) {
                            selectedDiscountIds = new Set(lineItem.eligibleDiscounts.map(d => d.primaryId).filter(Boolean));
                        }
                        setAppliedDiscountIds(
                            normalizeDiscountSelection(selectedDiscountIds, Array.from(discountsFromOrder.values()))
                        );
                    }

                    persistOrderData(obj);
                }
            }
        } catch {
            setAlertMsg({ open: true, message: "Failed to load order.", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const fetchDiscountsForOrder = async (order) => {
        if (!order) return false;

        const firstOrder = order.isGroupedOrder ? order.childOrders?.[0] : order.orderDetails?.[0];
        if (!firstOrder) return false;

        const scheduledProductId = firstOrder.lineItemList?.[0]?.scheduledProduct?.primaryId;
        const productId = firstOrder.lineItemList?.[0]?.scheduledProduct?.product?.primaryId;
        if (!scheduledProductId && !productId) return false;

        setIsFetchingDiscounts(true);
        try {
            const discountPayload = {
                associatedProducts: scheduledProductId
                    ? [{ primaryId: scheduledProductId, company: { primaryId: order.company?.primaryId || firstOrder.company?.primaryId } }]
                    : [],
                status: "ACTIVE",
                criteria: {
                    productAttributes: productId ? [{ primaryId: productId }] : [],
                    durationTypes: ["HOUR"]
                }
            };

            const result = await dispatch(findEligibleDiscounts(discountPayload));
            const nextFetchedDiscounts = dedupeDiscounts(normalizeDiscountResults(result));
            const availableDiscounts = dedupeDiscounts([
                ...nextFetchedDiscounts,
                ...Array.from(originalDiscountsMap.values())
            ]).filter(isDiscountValid);

            setFetchedDiscounts(nextFetchedDiscounts);
            setAppliedDiscountIds((prev) => normalizeDiscountSelection(prev, availableDiscounts));
            setPendingDiscountIds((prev) => normalizeDiscountSelection(prev, availableDiscounts));
            return true;
        } catch {
            setFetchedDiscounts([]);
            return false;
        } finally {
            setIsFetchingDiscounts(false);
        }
    };

    getOrderIdRef.current = getOrderId;
    fetchOrderDetailsRef.current = fetchOrderDetails;
    fetchDiscountsForOrderRef.current = fetchDiscountsForOrder;
    startSessionTimeoutRef.current = startSessionTimeout;

    // Fetch discounts only once when order first loads
    const persistOrderData = (d) => {
        try {
            if (d) localStorage.setItem('persistedOrder', JSON.stringify({ ...d, timestamp: Date.now() }));
        } catch { }
    };

    // ─── Discount helpers ──────────────────────────────────────────────────────
    const isDiscountValid = (d) => {
        if (!d?.primaryId) return false;
        if (d.eligible === false) return false;
        if (d.status && d.status !== 'ACTIVE') return false;
        const now = new Date();
        const { startDate, endDate } = getDiscountWindow(d);
        if (startDate && startDate > now) return false;
        if (endDate && endDate < now) return false;
        return true;
    };

    const getCompleteDiscountData = (discountId) => {
        const fromFetched = fetchedDiscounts.find(d => d.primaryId === discountId);
        if (fromFetched) return fromFetched;
        const fromOriginal = originalDiscountsMap.get(discountId);
        if (fromOriginal) return fromOriginal;
        return { primaryId: discountId, applyType: 'AMOUNT', amount: 0, name: 'Discount' };
    };

    const eligibleDiscounts = dedupeDiscounts([
        ...fetchedDiscounts,
        ...Array.from(originalDiscountsMap.values())
    ]).filter(isDiscountValid);

    const appliedDiscountsList = Array.from(appliedDiscountIds)
        .map(id => getCompleteDiscountData(id))
        .filter(d => d && d.primaryId);

    // ─── computeAmounts ────────────────────────────────────────────────────────
    const computeAmounts = () => {
        if (!orderDetails) return { subtotal: 0, discountAmt: 0, finalAmt: 0 };

        let subtotal = 0;
        let apiDueAmount = 0;
        let responseDiscountAmt = 0;

        if (orderDetails.isGroupedOrder && orderDetails.childOrders?.length > 0) {
            subtotal = resolveSubtotal(orderDetails);
            apiDueAmount = resolvePayable(orderDetails);
            responseDiscountAmt = getResponseDiscountAmount(orderDetails);
        } else {
            const order = orderDetails?.orderDetails?.[0];
            subtotal = resolveSubtotal(order);
            apiDueAmount = resolvePayable(order);
            responseDiscountAmt = getResponseDiscountAmount(order);
        }

        return { subtotal, discountAmt: responseDiscountAmt, finalAmt: apiDueAmount };
    };

    const { subtotal, discountAmt, finalAmt } = computeAmounts();
    const hasDiscount = discountAmt > 0;

    // ─── Formatting helpers ────────────────────────────────────────────────────
    const fmt = (v) => formatSharedNumber(roundMoney(v), companyState, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    const formatMoney = (v) => formatSharedCurrency(v, companyState);

    const timerText = () => {
        if (!remainingTime) return '15:00';
        if (remainingTime === 0) return 'Expired';
        return `${remainingTime.minutes}:${remainingTime.seconds.toString().padStart(2, '0')}`;
    };

    const timerCls = () => {
        if (!remainingTime || remainingTime === 0) return { pill: 'bg-red-100 border-red-300 text-red-700', dot: 'bg-red-500' };
        if (remainingTime.minutes < 5) return { pill: 'bg-amber-100 border-amber-300 text-amber-700', dot: 'bg-amber-500' };
        return { pill: 'bg-blue-50 border-blue-200 text-blue-700', dot: 'bg-blue-500' };
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => setSuccessMessage(''), 3000);
    };

    // ─── Discount modal handlers ───────────────────────────────────────────────
    const handleOpenOffersModal = () => {
        setPendingDiscountIds(normalizeDiscountSelection(appliedDiscountIds, eligibleDiscounts));
        setShowOffersModal(true);
    };

    const handleToggleDiscount = () => {
        const allDiscountIds = getAllDiscountIds(eligibleDiscounts);
        setPendingDiscountIds((prev) => (
            areDiscountSetsEqual(prev, allDiscountIds) ? new Set() : allDiscountIds
        ));
    };

    const buildDiscountRef = (d) => ({
        primaryId: d.primaryId,
        name: d.name || '',
        code: d.code || '',
        applyType: d.applyType || '',
        ...((d.applyType === 'AMOUNT' || d.applyType === 'FIXED') && { amount: d.amount }),
        ...(d.applyType === 'PERCENT' && { percentage: d.percentage }),
    });

    // Apply discounts to a single line-item list (immutable)
    const applyDiscountsToLineItems = (lineItemList = [], selected = []) =>
        lineItemList.map(item => {
            if (selected.length === 0) {
                const { discount: _d, discounts: _ds, eligibleDiscounts: _ed, ...rest } = item;
                return rest;
            }
            return {
                ...item,
                discounts: selected.map(buildDiscountRef),
                eligibleDiscounts: selected.map(buildDiscountRef),
            };
        });

    const buildChildOrderUpdatePayload = (childOrder, selectedDiscounts, now, modifiedBy) => {
        return {
            ...childOrder,
            primaryId: childOrder.primaryId,
            number: childOrder.number,
            accountId: childOrder.accountId,
            totalLineItems: childOrder.totalLineItems || childOrder.lineItemList?.length || 0,
            orderStatusHistoryList: childOrder.orderStatusHistoryList || [],
            company: childOrder.company || orderDetails.company,
            currentStatus: childOrder.currentStatus || "CREATED",
            isGroupedOrder: childOrder.isGroupedOrder || false,
            deliveryType: childOrder.deliveryType,
            child: childOrder.child || false,
            _id: childOrder._id,
            lineItemList: applyDiscountsToLineItems(childOrder.lineItemList, selectedDiscounts),
            modifiedBy,
            modifiedDate: now
        };
    };

    const buildGroupedOrderUpdatePayload = (selectedDiscounts, now, modifiedBy) => ({
        ...orderDetails,
        primaryId: orderDetails.primaryId,
        modifiedBy,
        modifiedDate: now,
        childOrders: orderDetails.childOrders.map(childOrder =>
            buildChildOrderUpdatePayload(childOrder, selectedDiscounts, now, modifiedBy)
        ),
    });

    const handleApplySelected = async () => {
        if (!orderDetails) return;
        setIsApplyingDiscount(true);

        try {
            const selectedDiscounts = eligibleDiscounts.filter(d => pendingDiscountIds.has(d.primaryId));
            const now = new Date().toISOString();
            const modifiedBy = userDetail?.email || 'system';

            if (orderDetails.isGroupedOrder && orderDetails.childOrders?.length > 0) {
                const groupedPayload = buildGroupedOrderUpdatePayload(selectedDiscounts, now, modifiedBy);
                const response = await dispatch(updateOrder(groupedPayload, "DISCOUNT_APPLY"));

                if (response) {
                    setAppliedDiscountIds(new Set(pendingDiscountIds));
                    await fetchOrderDetails(orderDetails.primaryId);
                    setShowOffersModal(false);
                    const count = pendingDiscountIds.size;
                    showSuccess(count > 0
                        ? `${count} discount${count !== 1 ? 's' : ''} applied to all ${orderDetails.childOrders.length} sessions!`
                        : 'Discounts removed from all sessions.'
                    );
                }
            } else {
                // Normal single order — unchanged behaviour
                const currentOrder = orderDetails.orderDetails[0];
                const orderUpdate = {
                    ...currentOrder,
                    lineItemList: applyDiscountsToLineItems(currentOrder.lineItemList, selectedDiscounts),
                    modifiedBy,
                    modifiedDate: now,
                };

                const response = await dispatch(updateOrder(orderUpdate, "DISCOUNT_APPLY"));
                if (response) {
                    setAppliedDiscountIds(new Set(pendingDiscountIds));
                    await fetchOrderDetails(currentOrder.primaryId);
                    setShowOffersModal(false);
                    const count = pendingDiscountIds.size;
                    showSuccess(count > 0
                        ? `${count} discount${count !== 1 ? 's' : ''} applied successfully!`
                        : 'Discounts removed successfully.'
                    );
                }
            }

        } catch {
            setAlertMsg({ open: true, message: "Failed to apply discounts. Please try again.", severity: "error" });
            setTimeout(() => setAlertMsg(prev => ({ ...prev, open: false })), 3000);
        } finally {
            setIsApplyingDiscount(false);
        }
    };

    const handleRemoveAllDiscounts = async () => {
        if (!orderDetails) return;
        setIsApplyingDiscount(true);

        try {
            const now = new Date().toISOString();
            const modifiedBy = userDetail?.email || 'system';

            if (orderDetails.isGroupedOrder && orderDetails.childOrders?.length > 0) {
                const groupedPayload = buildGroupedOrderUpdatePayload([], now, modifiedBy);
                const response = await dispatch(updateOrder(groupedPayload, "DISCOUNT_REMOVE"));

                if (response) {
                    setAppliedDiscountIds(new Set());
                    setPendingDiscountIds(new Set());
                    await fetchOrderDetails(orderDetails.primaryId);
                    showSuccess('All discounts removed from all sessions.');
                }
            } else {
                // Normal single order 
                const currentOrder = orderDetails.orderDetails[0];
                const response = await dispatch(updateOrder(
                    {
                        ...currentOrder,
                        lineItemList: applyDiscountsToLineItems(currentOrder.lineItemList, []),
                        modifiedBy,
                        modifiedDate: now,
                    },
                    "DISCOUNT_REMOVE"
                ));

                if (response) {
                    setAppliedDiscountIds(new Set());
                    setPendingDiscountIds(new Set());
                    await fetchOrderDetails(currentOrder.primaryId);
                    showSuccess('All discounts removed.');
                }
            }

        } catch {
            setAlertMsg({ open: true, message: "Failed to remove discounts.", severity: "error" });
            setTimeout(() => setAlertMsg(prev => ({ ...prev, open: false })), 3000);
        } finally {
            setIsApplyingDiscount(false);
        }
    };

    // ─── Navigation helpers ────────────────────────────────────────────────────
    const getCurrentOrderRecord = () => (
        orderDetails?.isGroupedOrder
            ? orderDetails
            : orderDetails?.orderDetails?.[0] || null
    );

    // Kiosk booking-success message needs to know whether to say "check in
    // today" vs. "watch for an email" — based on the booked slot's start date.
    const isOrderScheduledToday = (order) => {
        const scheduledProduct = getScheduledProductFromOrder(order);
        const effectiveDate = scheduledProduct?.period?.effectiveDate || {};
        const start = effectiveDate.localStartDate || effectiveDate.effectiveStartDate || effectiveDate.startDate;
        if (!start) return false;

        const startDate = new Date(start);
        const today = new Date();
        return startDate.getFullYear() === today.getFullYear()
            && startDate.getMonth() === today.getMonth()
            && startDate.getDate() === today.getDate();
    };

    const getCheckoutOrderNumber = () => {
        if (orderDetails?.isGroupedOrder) {
            return orderDetails?.number || orderDetails?.primaryId || getOrderId() || sessionStorage.getItem("orderPrimaryId");
        }

        return orderDetails?.orderDetails?.[0]?.number || orderDetails?.number || getOrderId() || sessionStorage.getItem("orderPrimaryId");
    };

    const getOfflineModifiedBy = () => {
        const fullName = `${userDetail?.firstName || ''} ${userDetail?.lastName || ''}`.trim();
        if (userDetail?.email) return userDetail.email;
        if (fullName) return fullName;

        try {
            const guestData = JSON.parse(sessionStorage.getItem("guestData") || "null");
            return guestData?.email || guestData?.primaryId || guestData?.id || "Guest";
        } catch {
            return localStorage.getItem("guestPrimaryId") || "Guest";
        }
    };

    const buildOfflineOrderUpdatePayload = ({ nextStatus, offlinePaymentMethod, zelleTransaction = null }) => {
        const currentOrder = getCurrentOrderRecord();
        if (!currentOrder?.primaryId) {
            throw new Error("Order details are missing.");
        }

        const modifiedBy = getOfflineModifiedBy();
        const modifiedDate = new Date().toISOString();
        const transactionList = zelleTransaction
            ? [...(currentOrder.transactionList || []), zelleTransaction]
            : currentOrder.transactionList;

        if (orderDetails?.isGroupedOrder) {
            return {
                ...currentOrder,
                currentStatus: nextStatus,
                paymentMethod: offlinePaymentMethod,
                modifiedBy,
                modifiedDate,
                ...(transactionList ? { transactionList } : {}),
                childOrders: (currentOrder.childOrders || []).map((childOrder) => ({
                    ...childOrder,
                    currentStatus: nextStatus,
                    paymentMethod: offlinePaymentMethod,
                    modifiedBy,
                    modifiedDate
                }))
            };
        }

        return {
            ...currentOrder,
            currentStatus: nextStatus,
            paymentMethod: offlinePaymentMethod,
            modifiedBy,
            modifiedDate,
            ...(transactionList ? { transactionList } : {})
        };
    };

    const syncOfflineOrderState = ({ nextStatus, offlinePaymentMethod, zelleTransaction = null }) => {
        setOrderDetails((prev) => {
            if (!prev) return prev;

            if (prev.isGroupedOrder) {
                const nextOrder = {
                    ...prev,
                    currentStatus: nextStatus,
                    paymentMethod: offlinePaymentMethod,
                    ...(zelleTransaction ? {
                        transactionList: [...(prev.transactionList || []), zelleTransaction]
                    } : {}),
                    childOrders: (prev.childOrders || []).map((childOrder) => ({
                        ...childOrder,
                        currentStatus: nextStatus,
                        paymentMethod: offlinePaymentMethod
                    }))
                };
                persistOrderData(nextOrder);
                return nextOrder;
            }

            if (!prev.orderDetails?.length) return prev;

            const nextSingleOrder = {
                ...prev.orderDetails[0],
                currentStatus: nextStatus,
                paymentMethod: offlinePaymentMethod,
                ...(zelleTransaction ? {
                    transactionList: [...(prev.orderDetails[0].transactionList || []), zelleTransaction]
                } : {})
            };

            const nextOrder = {
                ...prev,
                orderDetails: [nextSingleOrder, ...prev.orderDetails.slice(1)]
            };
            persistOrderData(nextOrder);
            return nextOrder;
        });
    };

    // ─── Payment handlers ──────────────────────────────────────────────────────

    const submitPayment = async (paymentData, protocol, returnURL, countryCode) => {
        setLoading(true);
        setIsProcessingPayment(true);
        clearSelectionTimeout();
        try {
            await setReturnURL(protocol, returnURL, countryCode);
            const res = await initiatePayment(paymentData);
            if (!res?.data) throw new Error("Payment gateway did not respond");
            const url = res.data.stripeResponse || res.data.razorPayResponse;
            if (!url) throw new Error("No payment URL received");
            setBookingProcessingNotice(getOrderId(), paymentData?.orderNumber);
            window.location.href = url;
        } catch (e) {
            setLoading(false);
            setIsProcessingPayment(false);
            if (orderDetails) startSessionTimeout();
            setAlertMsg({ open: true, message: e.message || "Payment failed", severity: "error" });
            setTimeout(() => setAlertMsg(prev => ({ ...prev, open: false })), 3000);
        }
    };

    const handleCashPaymentConfirm = async () => {
        try {
            setLoading(true);
            setIsProcessingPayment(true);
            clearSelectionTimeout();

            const currentOrder = getCurrentOrderRecord();
            const orderId = currentOrder?.primaryId || getOrderId() || sessionStorage.getItem("orderPrimaryId");
            if (!orderId) throw new Error("Order ID missing.");

            const nextStatus = getOfflineOrderStatus('cash');
            const orderUpdatePayload = buildOfflineOrderUpdatePayload({
                nextStatus,
                offlinePaymentMethod: "CASH"
            });
            await dispatch(updateOrder(orderUpdatePayload, "order_status_update"));

            setShowCashConfirmDialog(false);
            syncOfflineOrderState({
                nextStatus,
                offlinePaymentMethod: "CASH"
            });
            setBookingProcessingNotice(orderId, getCheckoutOrderNumber());
            setSuccessMessage("Booking saved as payment pending.");
            if (isKiosk) {
                setShowKioskBookingSuccess(true);
            } else {
                successTimeoutRef.current = setTimeout(() => navigate('/my-bookings'), 1800);
            }
        } catch (e) {
            if (orderDetails) startSessionTimeout();
            setAlertMsg({ open: true, message: e.message || "Failed to confirm cash booking.", severity: "error" });
            setTimeout(() => setAlertMsg(prev => ({ ...prev, open: false })), 3000);
        } finally {
            setLoading(false);
            setIsProcessingPayment(false);
        }
    };

    const handleZellePaymentConfirm = async () => {
        try {
            if (!normalizedZelleReferenceId) {
                throw new Error("Enter the Zelle reference ID before continuing.");
            }

            setLoading(true);
            setIsProcessingPayment(true);
            clearSelectionTimeout();

            const currentOrder = getCurrentOrderRecord();
            const orderId = currentOrder?.primaryId || getOrderId() || sessionStorage.getItem("orderPrimaryId");
            if (!orderId) throw new Error("Order ID missing.");

            const modifiedBy = getOfflineModifiedBy();
            const orderNumber = getCheckoutOrderNumber();
            const zelleTransaction = buildZellePendingTransaction({
                amount: finalAmt,
                orderPrimaryId: orderId,
                accountPrimaryId: userDetail?.account?.primaryId || currentOrder?.accountId,
                userPrimaryId: userDetail?.primaryId || currentOrder?.userPrimaryId || currentOrder?.user?.primaryId,
                company: storedCompanyDetails,
                zellePaymentMethod,
                paymentMethodConfig: manualPaymentOption?.paymentMethodConfig,
                referenceId: normalizedZelleReferenceId,
                createdBy: modifiedBy
            });

            const nextStatus = getOfflineOrderStatus(manualPaymentOption?.value || 'zelle');
            const orderUpdatePayload = buildOfflineOrderUpdatePayload({
                nextStatus,
                offlinePaymentMethod: manualPaymentOption?.orderPaymentMethod || "ZELLE",
                zelleTransaction
            });

            await dispatch(updateOrder(orderUpdatePayload, "order_status_update"));

            setShowZelleModal(false);
            setZelleReferenceId('');
            syncOfflineOrderState({
                nextStatus,
                offlinePaymentMethod: manualPaymentOption?.orderPaymentMethod || "ZELLE",
                zelleTransaction
            });
            setBookingProcessingNotice(orderId, orderNumber);
            setSuccessMessage(`${manualPaymentOption?.title || "Manual"} payment submitted successfully. Your payment is now processing.`);
            if (isKiosk) {
                setShowKioskBookingSuccess(true);
            } else {
                successTimeoutRef.current = setTimeout(() => navigate('/my-bookings'), 1800);
            }
        } catch (e) {
            if (orderDetails) startSessionTimeout();
            setAlertMsg({ open: true, message: e.message || "Failed to submit Zelle payment.", severity: "error" });
            setTimeout(() => setAlertMsg(prev => ({ ...prev, open: false })), 3000);
        } finally {
            setLoading(false);
            setIsProcessingPayment(false);
        }
    };

    const handleCheckout = async () => {
        if (remainingTime === 0) {
            setAlertMsg({ open: true, message: "Session expired. Please restart.", severity: "error" });
            setTimeout(() => setAlertMsg(prev => ({ ...prev, open: false })), 3000);
            return;
        }

        try {
            const orderId = getOrderId() || sessionStorage.getItem("orderPrimaryId");
            if (!orderId) throw new Error("Order ID missing.");

            if (isCashPaymentOption(selectedPaymentOption)) { setShowCashConfirmDialog(true); return; }
            if (requiresOfflineReference(selectedPaymentOption)) { setShowZelleModal(true); return; }

            sessionStorage.removeItem("pendingBooking");
            const gw = getGatewayProvider(paymentMethod, companyOperatingLocation, companyPaymentMethods);
            const currency = getCurrencyForCountry(companyOperatingLocation);

            const amount = Math.floor(finalAmt * 100) / 100;

            sessionStorage.setItem("totalAmountDue", amount);

            const orderNumber = getCheckoutOrderNumber();

            const paymentData = {
                paymentMethod: "PAYMENT_GATEWAY",
                currency,
                countryCode: companyOperatingLocation,
                orderNumber,
                amount,
                accountId: userDetail?.account?.primaryId,
                userId: userDetail.primaryId,
                transactionType: "PURCHASE",
                paymentGatewayData: {
                    gatewayProvider: gw,
                    customerName: `${userDetail.firstName} ${userDetail.lastName}`,
                    customerEmail: userDetail.email
                },
                company: {
                    companyName: storedCompanyDetails.name,
                    primaryId: storedCompanyDetails.primaryId,
                    domain: { type: storedCompanyDetails.domain.type },
                    account: { paymentMethods: storedCompanyDetails.account?.paymentMethods || [] }
                },
                ...(appliedDiscountIds.size > 0 && { discountIds: Array.from(appliedDiscountIds) })
            };

            submitPayment(paymentData, window.location.protocol, window.location.host, companyOperatingLocation);
        } catch (e) {
            setLoading(false);
            setIsProcessingPayment(false);
            setAlertMsg({ open: true, message: e.message, severity: "error" });
            setTimeout(() => setAlertMsg(prev => ({ ...prev, open: false })), 3000);
        }
    };

    // ─── Derive PaymentSummaryCard props ──────────────────────────────────────
    const getBreakdownLineItems = () => {
        let tax = 0, ship = 0, dur = 0, pph = 0, groupedTotal = 0, sessionCount = 0, groupedHours = 0;

        if (orderDetails?.isGroupedOrder && orderDetails.childOrders?.length > 0) {
            const firstChild = orderDetails.childOrders[0];
            const li = firstChild.lineItemList?.[0];
            const sp = li?.scheduledProduct;
            const product = sp?.product;
            dur = parseFloat(sp?.period?.duration) || parseFloat(li?.quantity) || 0;
            pph = parseFloat(product?.price?.priceAmt) || 0;
            sessionCount = orderDetails.childOrders.length;
            orderDetails.childOrders.forEach(child => {
                const childLineItem = child?.lineItemList?.[0];
                tax += parseFloat(child.tax) || 0;
                ship += parseFloat(child.shippingAndHandlingAmount) || 0;
                groupedTotal += resolveSubtotal(child);
                groupedHours += parseFloat(childLineItem?.quantity) || parseFloat(childLineItem?.scheduledProduct?.period?.duration) || 0;
            });
        } else {
            const order = orderDetails?.orderDetails?.[0];
            const li = order?.lineItemList?.[0];
            const sp = li?.scheduledProduct;
            const product = sp?.product;
            tax = parseFloat(order?.tax) || 0;
            ship = parseFloat(order?.shippingAndHandlingAmount) || 0;
            dur = parseFloat(sp?.period?.duration) || parseFloat(li?.quantity) || 0;
            pph = parseFloat(product?.price?.priceAmt) || 0;
        }

        return { tax, ship, dur, pph, groupedTotal, sessionCount, groupedHours };
    };

    // ─── Guard renders ────────────────────────────────────────────────────────
    if (loading && !orderDetails) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/30 p-4">
                <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mb-6"><Spinner size={10} /></div>
                <p className="text-xl font-bold text-slate-800">Loading your order</p>
                <p className="text-sm text-slate-500 mt-1">Please wait a moment…</p>
            </div>
        );
    }

    if (!orderDetails) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/30 p-4">
                <div className="text-center max-w-md bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
                    <div className="flex justify-center mb-6"><Spinner size={12} /></div>
                    <p className="text-xl font-bold text-slate-800 mb-2">{alertMsg.open ? alertMsg.message : "Order data missing or expired"}</p>
                    <p className="text-sm text-slate-500 mb-6">Please restart your order</p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                localStorage.removeItem('persistedOrder');
                                localStorage.removeItem('orderDetails');
                                sessionStorage.removeItem('orderPrimaryId');
                                window.location.reload();
                            }}
                            className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-md shadow-blue-200 hover:from-blue-700 hover:to-indigo-700 transition-all hover:scale-105 active:scale-95"
                        >
                            Refresh
                        </button>
                        <button
                            onClick={() => navigate('/')}
                            className="flex-1 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold transition-all hover:border-slate-300"
                        >
                            Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (businessdomain !== "FLEX_OFFICE") return null;

    const { tax, ship, dur, pph, groupedTotal, sessionCount, groupedHours } = getBreakdownLineItems();

    // Mobile-only sticky CTA label — mirrors PaymentSummaryCard's own button label
    // logic so the pinned bottom bar reads the same as the in-card button.
    const mobileCtaLabel = isCashPaymentOption(selectedPaymentOption)
        ? 'Confirm Cash Booking'
        : requiresOfflineReference(selectedPaymentOption)
            ? `Open ${selectedPaymentOption?.title || selectedPaymentOption?.label || 'QR Code'}`
            : selectedPaymentOption?.title || selectedPaymentOption?.label || 'Pay Now';
    const isCheckoutDisabled = loading || isProcessingPayment || !paymentMethod || !selectedPaymentOption || remainingTime === 0;

    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            {/* Toasts */}
            <SessionExpiredToast
                show={showExpiredNotification}
                onClose={() => setShowExpiredNotification(false)}
                onNavigateHome={() => { setShowExpiredNotification(false); navigate('/'); }}
            />
            <SuccessToast message={successMessage} />

            {showKioskBookingSuccess && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 px-4">
                    <div className="w-full max-w-lg overflow-hidden rounded-[2rem] bg-white shadow-2xl">
                        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-8 pb-10 pt-12 text-center">
                            <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-white/15 ring-4 ring-white/40">
                                <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h1 className="mt-6 text-3xl font-bold text-white">You're all booked!</h1>
                        </div>

                        <div className="px-8 py-8 text-center">
                            <p className="text-lg text-slate-700">
                                {isOrderScheduledToday(getCurrentOrderRecord())
                                    ? "Your booking is confirmed for today. You can check in now — please see our staff at the front desk."
                                    : "Your booking is confirmed. You'll receive an email confirmation with all the details."}
                            </p>
                            <p className="mt-4 text-base text-slate-500">Thank you for booking with us!</p>

                            <button
                                type="button"
                                onClick={() => {
                                    setShowKioskBookingSuccess(false);
                                    navigate('/kiosk', { replace: true });
                                }}
                                className="mt-8 min-h-[64px] w-full rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-lg font-bold text-white shadow-lg shadow-blue-200 transition-all hover:from-blue-700 hover:to-indigo-700"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            <OffersModal
                open={showOffersModal}
                onClose={() => setShowOffersModal(false)}
                discounts={eligibleDiscounts}
                selectedDiscountIds={pendingDiscountIds}
                onToggle={handleToggleDiscount}
                onApplySelected={handleApplySelected}
                isApplying={isApplyingDiscount}
                formatMoney={formatMoney}
                subtotal={subtotal}
                isGuestUser={isGuestUser}
            />

            <ExpiryWarningDialog
                show={showExpiryWarningDialog}
                remainingTime={remainingTime}
                onContinue={() => setShowExpiryWarningDialog(false)}
                onCancelBooking={() => {
                    setShowExpiryWarningDialog(false);
                    clearSelectionTimeout();
                    navigate('/flex-home');
                    localStorage.removeItem('persistedOrder');
                    localStorage.removeItem('orderDetails');
                    sessionStorage.removeItem('orderPrimaryId');
                }}
            />

            {showCashConfirmDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
                    <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl border border-slate-200">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Cash Payment</p>
                                <h3 className="mt-2 text-xl font-bold text-slate-900">Confirm cash booking</h3>
                                <p className="mt-2 text-sm text-slate-500">
                                    This will confirm the booking now and mark the payment as cash collected offline.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowCashConfirmDialog(false)}
                                className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                            >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mt-5 rounded-2xl bg-slate-50 p-4">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Order</span>
                                <span className="font-semibold text-slate-800">
                                    {orderDetails?.number || orderDetails?.orderDetails?.[0]?.number || "Pending"}
                                </span>
                            </div>
                            <div className="mt-3 flex items-center justify-between text-sm">
                                <span className="text-slate-500">Amount</span>
                                <span className="font-semibold text-slate-900">{formatMoney(finalAmt)}</span>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowCashConfirmDialog(false)}
                                className={`flex-1 rounded-xl border border-slate-200 font-semibold text-slate-700 hover:bg-slate-50 ${isKiosk ? 'px-4 py-4 text-base' : 'px-4 py-3 text-sm'}`}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCashPaymentConfirm}
                                className={`flex-1 rounded-xl bg-emerald-600 font-semibold text-white hover:bg-emerald-700 ${isKiosk ? 'px-4 py-4 text-base' : 'px-4 py-3 text-sm'}`}
                            >
                                Confirm Cash Booking
                            </button>
                        </div>
                    </div>
                </div>
            )}

 {showZelleModal && (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4 sm:p-6">
        <div className="mx-auto flex min-h-full w-full max-w-4xl items-center justify-center">
        <div className="flex w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-2xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]">

            {/* Header */}
            <div className="shrink-0 flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">Z</div>
                    <div>
                        <h3 className="text-sm font-semibold text-slate-900">{manualPaymentDetails.title}</h3>
                        <p className="text-xs text-slate-400">Scan or enter details to complete</p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setShowZelleModal(false)}
                    disabled={loading || isProcessingPayment}
                    className="w-7 h-7 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition disabled:opacity-40"
                >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Body — two columns */}
            <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 gap-0 divide-y divide-slate-100 md:grid-cols-2 md:divide-x md:divide-y-0">

                {/* Left — QR */}
                <div className="flex flex-col items-center justify-center px-6 py-6 bg-blue-50">
                    <div className="w-44 h-44 rounded-2xl bg-white border border-blue-100 p-2 shadow-sm overflow-hidden">
                        {manualPaymentDetails.qrImageUrl ? (
                            <img
                                src={manualPaymentDetails.qrImageUrl}
                                alt={`${manualPaymentDetails.title} QR code`}
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center text-center text-sm text-slate-500">
                                Use the saved recipient details to complete the payment.
                            </div>
                        )}
                    </div>
                    <p className="mt-4 text-xs text-blue-600 text-center font-medium leading-relaxed">
                        {manualPaymentDetails.qrImageUrl
                            ? "Scan with your banking app"
                            : hasConfiguredRecipientDetails
                                ? "Use the payment details shown"
                                : "Complete the payment in your banking app"}
                    </p>
                    {hasConfiguredRecipientDetails && (
                        <p className="mt-1 text-[11px] text-slate-400 text-center leading-relaxed">
                            or use the recipient details on the right
                        </p>
                    )}
                </div>

                {/* Right — Details */}
                <div className="flex flex-col px-6 py-6 gap-4">

                    {/* Recipient info */}
                    {(configuredRecipientName || configuredReferenceLabel) && (
                        <div className="rounded-xl bg-blue-600 px-4 py-3 text-white">
                            <p className="text-[9px] uppercase tracking-widest text-blue-200 mb-1">Recipient</p>
                            {configuredRecipientName && (
                                <p className="text-sm font-semibold leading-tight">{configuredRecipientName}</p>
                            )}
                            {configuredReferenceLabel && (
                                <p className="text-xs text-blue-200 mt-0.5">{configuredReferenceLabel}</p>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-2">
                        {configuredRecipientTag && (
                            <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wide">Zelle Tag</p>
                                <p className="text-sm font-semibold text-slate-900 mt-0.5 break-words">{configuredRecipientTag}</p>
                            </div>
                        )}
                        {configuredRecipientPhone && (
                            <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wide">Phone Number</p>
                                <p className="text-sm font-semibold text-slate-900 mt-0.5">
                                    {[configuredRecipientCountryCode, configuredRecipientPhone].filter(Boolean).join(' ')}
                                </p>
                            </div>
                        )}
                        {configuredRecipientEmail && (
                            <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wide">Email</p>
                                <p className="text-sm font-semibold text-slate-900 mt-0.5 break-all">{configuredRecipientEmail}</p>
                            </div>
                        )}
                    </div>

                    {/* Amount + Ref */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Amount</p>
                            <p className="text-sm font-semibold text-slate-900 mt-0.5">{formatMoney(finalAmt)}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Booking Ref</p>
                            <p className="text-xs font-semibold text-slate-900 mt-0.5 truncate">
                                {orderDetails?.number || orderDetails?.orderDetails?.[0]?.number || 'Pending'}
                            </p>
                        </div>
                    </div>

                    {/* Steps */}
                    <div className="space-y-2">
                        {[
                            'Open your payment app or banking app.',
                            manualPaymentDetails.qrImageUrl
                                ? hasConfiguredRecipientDetails
                                    ? 'Scan the QR or use the saved payment details above.'
                                    : 'Scan the QR above to complete the payment.'
                                : hasConfiguredRecipientDetails
                                    ? 'Use the saved payment details above.'
                                    : 'Complete the payment using the configured payment method.',
                            'Send exact amount with booking ref in memo.',
                            'Paste your confirmation ID below.',
                        ].map((step, i) => (
                            <div key={i} className="flex items-start gap-2">
                                <span className="flex-shrink-0 w-4 h-4 mt-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold flex items-center justify-center">
                                    {i + 1}
                                </span>
                                <p className="text-xs text-slate-500 leading-relaxed">{step}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            </div>

            {/* Reference ID + Footer */}
            <div className="shrink-0 px-6 py-4 border-t border-slate-100 bg-slate-50/60 space-y-3">
                <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        {manualPaymentDetails.title} Reference ID
                    </label>
                    <input
                        type="text"
                        value={zelleReferenceId}
                        onChange={(e) => setZelleReferenceId(e.target.value)}
                        placeholder={`Paste your ${manualPaymentDetails.title} confirmation or reference ID`}
                        disabled={loading || isProcessingPayment}
                        className={`w-full rounded-xl border border-slate-200 bg-white text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-300 disabled:bg-slate-100 disabled:cursor-not-allowed ${isKiosk ? 'px-4 py-3.5 text-sm' : 'px-4 py-2.5 text-xs'}`}
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setShowZelleModal(false)}
                        disabled={loading || isProcessingPayment}
                        className={`min-w-0 flex-1 rounded-xl border border-slate-200 bg-white font-semibold text-slate-600 hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed ${isKiosk ? 'px-4 py-3.5 text-sm' : 'px-4 py-2.5 text-xs'}`}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleZellePaymentConfirm}
                        disabled={loading || isProcessingPayment || !normalizedZelleReferenceId}
                        className={`min-w-0 flex-[2] rounded-xl bg-blue-600 font-semibold text-white hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed ${isKiosk ? 'px-4 py-3.5 text-sm' : 'px-4 py-2.5 text-xs'}`}
                    >
                        {loading || isProcessingPayment ? 'Confirming…' : manualPaymentDetails.confirmLabel}
                    </button>
                </div>
            </div>
        </div>
        </div>
    </div>
)}

            {/* Page */}
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-indigo-50/30 pt-24 pb-16 px-4 sm:px-6 lg:px-8 fx-m-page fx-m-bottom-safe-area">
                <div className="max-w-7xl mx-auto fx-m-full-bleed">

                    {/* Page Header */}
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8 gap-4">
                        <div>
                            <h1 className="text-3xl font-extrabold text-slate-900 mb-1">
                                Hey <span className="text-slate-900">{displayName}</span>!
                            </h1>
                            <p className="text-slate-500">
                                {isGuestUser
                                    ? 'Review your booking details and available offers before payment.'
                                    : 'Review your booking and apply any discounts before payment.'}
                            </p>
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate(-1)}
                            disabled={loading || isProcessingPayment}
                            className={`inline-flex items-center gap-2 bg-white text-slate-700 font-semibold rounded-xl border border-slate-200 hover:bg-slate-50 shadow-sm transition-all disabled:opacity-50 ${isKiosk ? 'px-6 py-3.5 text-base' : 'px-5 py-2.5'}`}
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back
                        </motion.button>
                    </div>

                    {loading && <Loading message="Processing payment, please wait…" />}

                    {/* Alert Banner */}
                    {alertMsg.open && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`mb-6 p-4 rounded-xl border flex items-center gap-3 shadow-sm ${alertMsg.severity === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
                                alertMsg.severity === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                                    'bg-blue-50 border-blue-200 text-blue-800'
                                }`}
                        >
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="font-semibold text-sm flex-1">{alertMsg.message}</p>
                            <button onClick={() => setAlertMsg(p => ({ ...p, open: false }))}>
                                <svg className="w-4 h-4 opacity-60 hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </motion.div>
                    )}


                    {/* Main Grid */}
                    <div className="grid lg:grid-cols-3 gap-6 items-start">
                        {/* Left: Booking Details */}
                        <div className="lg:col-span-2 space-y-6">
                            <OrderDetailsCard
                                orderDetails={orderDetails}
                                remainingTime={remainingTime}
                                timerText={timerText()}
                                timerCls={timerCls()}
                                companyState={companyState}
                                operatingLocationCode={operatingLocationCode}
                                defaultProductImage={defaultProductImage}
                            />
                        </div>

                        {/* Right: Payment Summary + Trust Badges */}
                        <div className="space-y-4">
                            <PaymentSummaryCard
                                orderDetails={orderDetails}
                                defaultProductImage={defaultProductImage}
                                fmt={fmt}
                                formatMoney={formatMoney}
                                subtotal={subtotal}
                                discountAmt={discountAmt}
                                finalAmt={finalAmt}
                                hasDiscount={hasDiscount}
                                tax={tax}
                                ship={ship}
                                pph={pph}
                                dur={dur}
                                isGroupedOrder={orderDetails?.isGroupedOrder}
                                groupedTotal={groupedTotal}
                                sessionCount={sessionCount}
                                groupedHours={groupedHours}
                                shoppingCartData={shoppingCartData}
                                cartTotalPrice={cartTotalPrice}
                                isFetchingDiscounts={isFetchingDiscounts}
                                eligibleDiscounts={eligibleDiscounts}
                                appliedDiscountIds={appliedDiscountIds}
                                appliedDiscountsList={appliedDiscountsList}
                                isApplyingDiscount={isApplyingDiscount}
                                remainingTime={remainingTime}
                                loading={loading}
                                isProcessingPayment={isProcessingPayment}
                                paymentMethod={paymentMethod}
                                paymentOptions={availablePaymentOptions}
                                hideCashPaymentOption={shouldHideCashPaymentOption}
                                onPaymentMethodChange={setPaymentMethod}
                                onOpenOffersModal={handleOpenOffersModal}
                                onRemoveAllDiscounts={handleRemoveAllDiscounts}
                                onCheckout={handleCheckout}
                                onOpenZelleModal={() => setShowZelleModal(true)}
                                isGuestUser={isGuestUser}
                            />
                            {/* {showZelleQuickAccess && (
                                <div className="overflow-hidden rounded-2xl border border-violet-200 bg-white shadow-sm">
                                    <div className="bg-gradient-to-r from-violet-600 via-fuchsia-600 to-indigo-700 px-5 py-4 text-white">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">Zelle Access</p>
                                                <h3 className="mt-1 text-base font-bold">Pay quickly with QR</h3>
                                            </div>
                                            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 text-base font-black">
                                                Z
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-center gap-4">
                                            <div className="h-24 w-24 shrink-0 rounded-2xl border border-violet-100 bg-violet-50 p-2 flex items-center justify-center">
                                                <img
                                                    src={ZELLE_QR_IMAGE_URL}
                                                    alt="Zelle QR"
                                                    className="h-full w-full object-contain rounded-lg"
                                                />
                                            </div>

                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-slate-900">
                                                    {zelleRecipientName}
                                                </p>
                                                <p className="mt-1 text-xs text-slate-500">
                                                    {zelleReferenceLabel}
                                                </p>
                                                <p className="mt-2 text-xs font-medium text-violet-600">
                                                    Reference ID required after payment
                                                </p>
                                                <p className="mt-2 text-sm font-bold text-violet-700">
                                                    {formatMoney(finalAmt)}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowZelleModal(true)}
                                            className="mt-4 w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                                        >
                                            Open Zelle Payment
                                        </button>
                                    </div>
                                </div>
                            )} */}
                            <TrustBadges />
                        </div>
                    </div>
                </div>
            </div>
            {showZelleQuickAccess && (
                <div
                    className="fixed bottom-5 right-5 z-40 lg:hidden"
                    style={!isKiosk ? { bottom: 'calc(58px + 96px + env(safe-area-inset-bottom, 0px))' } : undefined}
                >
                    <button
                        type="button"
                        onClick={() => setShowZelleModal(true)}
                        className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-indigo-700 px-4 py-3 text-sm font-semibold text-white shadow-xl"
                    >
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 font-black">Z</span>
                        Open Zelle Payment
                    </button>
                </div>
            )}

            {/* Mobile/tablet-only pinned CTA — the order details above this can run
                long (address, recurring schedule, etc.), so on phones/tablets the
                primary "Pay" action stays reachable without scrolling all the way
                down to the payment card. Desktop (lg+) is untouched — the in-card
                button there is already always visible in the 3-column layout.
                Excluded entirely during an active kiosk session (`!isKiosk`): a
                kiosk device can legitimately be a portrait tablet under 1024px
                wide, and this is a new, unrequested UI element — kiosk's existing
                payment button/flow must stay exactly as it was. */}
            {!showKioskBookingSuccess && !isKiosk && (
                <div
                    className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-sm px-4 pt-3 pb-3 shadow-[0_-4px_16px_rgba(15,23,42,0.08)]"
                    style={{ bottom: 'calc(58px + env(safe-area-inset-bottom, 0px))' }}
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Total Due</p>
                            <p className="truncate text-lg font-extrabold text-slate-900">{formatMoney(finalAmt)}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => { triggerHaptic(); handleCheckout(); }}
                            disabled={isCheckoutDisabled}
                            className="flex-1 max-w-[240px] rounded-xl bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isProcessingPayment ? 'Processing…' : mobileCtaLabel}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ConfirmBook;
